import { useMemo } from 'react'
import { Box } from '@mui/material'
import {
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridApiPremium,
} from '@mui/x-data-grid-premium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { WarehouseInboundRow } from './WarehouseInboundReport.types'
import { summaryXlsToolbar } from './WarehouseInboundReport.Toolbar'

type UomValue = { value: number; uom: string }

const sumByCas: GridAggregationFunction<UomValue, number | null> = {
  label: 'sum',
  columnTypes: ['number'],
  getCellValue: ({ row, field }) => ({
    value: Number(row[field]) || 0,
    uom: row.uom as string,
  }),
  apply: ({ values }) => {
    const typed = values.filter((v): v is UomValue => v != null)
    if (typed.length === 0) return null

    const casValues = typed.filter((v) => v.uom === 'CAS')
    if (casValues.length > 0) {
      return casValues.reduce((sum, v) => sum + v.value, 0)
    }

    const firstUom = typed[0].uom
    return typed
      .filter((v) => v.uom === firstUom)
      .reduce((sum, v) => sum + v.value, 0)
  },
}

type WarehouseInboundSummaryMUITableProps = {
  data: (WarehouseInboundRow & { id: number })[]
  weeks: string[]
  isLoading: boolean
  apiRefCallback: (apiRef: React.RefObject<GridApiPremium>) => void
}

type SummaryRow = {
  id: string
  baseSourceSiteCode: string
  destinationSiteCode: string
  uom: string
} & Record<string, string | number>

export const WarehouseInboundSummaryMUITable = ({
  data,
  weeks,
  isLoading,
  apiRefCallback,
}: WarehouseInboundSummaryMUITableProps) => {
  const summaryRows: SummaryRow[] = useMemo(() => {
    const grouped = new Map<string, SummaryRow>()
    for (const row of data) {
      const key = `${row.baseSourceSiteCode}|${row.destinationSiteCode}|${row.uom}`
      let agg = grouped.get(key)
      if (!agg) {
        agg = {
          id: key,
          baseSourceSiteCode: row.baseSourceSiteCode,
          destinationSiteCode: row.destinationSiteCode,
          uom: row.uom,
        }
        for (const w of weeks) agg[w] = 0
        grouped.set(key, agg)
      }
      for (const w of weeks) {
        agg[w] = (Number(agg[w]) || 0) + (Number(row[w]) || 0)
      }
    }
    return Array.from(grouped.values())
  }, [data, weeks])

  const columns: ExtendedGridColDef[] = useMemo(() => {
    const dimensionCols: ExtendedGridColDef[] = [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site',
        width: 160,
        type: 'string',
      },
      {
        field: 'destinationSiteCode',
        headerName: 'Destination Site',
        width: 160,
        type: 'string',
      },
      { field: 'uom', headerName: 'UOM', width: 80, type: 'string' },
    ]
    const weekCols: ExtendedGridColDef[] = weeks.map((w) => ({
      field: w,
      headerName: w,
      type: 'number' as const,
      width: 100,
      align: 'right' as const,
      headerAlign: 'right' as const,
      valueFormatter: (value: number) =>
        value != null && value !== 0 ? value.toFixed(2) : '',
    }))
    const grandTotalCol: ExtendedGridColDef = {
      field: 'grandTotal',
      headerName: 'Grand Total',
      type: 'number' as const,
      width: 130,
      align: 'right' as const,
      headerAlign: 'right' as const,
      valueGetter: (_value: unknown, row: Record<string, unknown>) =>
        weeks.reduce((sum, w) => sum + (Number(row[w]) || 0), 0),
      valueFormatter: (value: number) =>
        value != null && value !== 0 ? value.toFixed(2) : '',
    }
    return [...dimensionCols, ...weekCols, grandTotalCol]
  }, [weeks])

  const initialState = useMemo(
    () => ({
      aggregation: {
        model: {
          ...Object.fromEntries(weeks.map((w) => [w, 'sumByCas'])),
          grandTotal: 'sumByCas',
        },
      },
      sorting: {
        sortModel: [
          { field: 'baseSourceSiteCode', sort: 'desc' as const },
          { field: 'destinationSiteCode', sort: 'desc' as const },
        ],
      },
      pagination: { paginationModel: { pageSize: 100 } },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <Box
      sx={{
        scrollbarWidth: 'thin',
        overflowX: 'auto',
        maxWidth: '97vw',
      }}
    >
      <BaseTable
        data={summaryRows}
        columns={columns}
        isLoading={isLoading}
        updateTableData={() => {}}
        isSaving={false}
        aggregationFunctions={{ ...GRID_AGGREGATION_FUNCTIONS, sumByCas }}
        initialState={initialState}
        downloadFileName="WarehouseInboundForecastSummaryReport"
        apiRefCallback={apiRefCallback}
        enableBlocker={false}
        customToolbar={summaryXlsToolbar}
      />
    </Box>
  )
}
